import React, { useEffect, useState, useMemo } from "react";
import {
  addDoc,
  collection,
  getDocs,
  query,
  where,
  serverTimestamp,
} from "firebase/firestore";
import { db } from "../firebase";
import { motion, AnimatePresence } from "framer-motion";
import {
  User,
  Phone,
  Mail,
  MapPin,
  CalendarDays,
  Plus,
  CheckCircle,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";
import heroBg from "../assets/hearse.jpg"; 

/* ================= MAIN COMPONENT ================= */
export default function BookHearse() {
  const [dark, setDark] = useState(true);

  /* ---------- FORM ---------- */
  const [form, setForm] = useState({
    name: "",
    phone: "",
    email: "",
    location: "",
    eventStart: "",
    eventEnd: "",
    carId: "",
    route: "",
  });

  /* ---------- ROUTE ---------- */
  const [routeInput, setRouteInput] = useState("");
  const [routeStops, setRouteStops] = useState([]);

  /* ---------- DATA ---------- */
  const [cars, setCars] = useState([]);
  const [blockedCars, setBlockedCars] = useState([]);

  /* ---------- PAGINATION ---------- */
  const CARS_PER_PAGE = 6;
  const [page, setPage] = useState(0);

  /* ---------- UI ---------- */
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState("");

  /* ================= THEME ================= */
  useEffect(() => {
    const t = localStorage.getItem("theme");
    setDark(t !== "light");
  }, []);

  const theme = {
    page: dark ? "bg-[#0a0a0a] text-white" : "bg-gray-100 text-gray-900",
    card: dark ? "bg-white/5 border-white/10" : "bg-white border-gray-200",
    input: dark
      ? "bg-black/40 border-white/20 text-white"
      : "bg-white border-gray-400 text-gray-900",
  };

  /* ================= LOAD CARS ================= */
  useEffect(() => {
    getDocs(collection(db, "cars")).then((snap) =>
      setCars(snap.docs.map((d) => ({ id: d.id, ...d.data() })))
    );
  }, []);

  /* ================= AVAILABILITY (DATE RANGE) ================= */
  useEffect(() => {
    if (!form.eventStart || !form.eventEnd) {
      setBlockedCars([]);
      return;
    }

    const check = async () => {
      const q = query(
        collection(db, "bookings"),
        where("status", "==", "Approved")
      );
      const snap = await getDocs(q);

      const start = new Date(form.eventStart);
      const end = new Date(form.eventEnd);

      const blocked = [];
      snap.docs.forEach((d) => {
        const b = d.data();
        if (!b.eventStart || !b.eventEnd) return;

        const bs = new Date(b.eventStart);
        const be = new Date(b.eventEnd);

        if (bs <= end && be >= start) {
          blocked.push(b.carId);
        }
      });

      setBlockedCars(blocked);
    };

    check();
  }, [form.eventStart, form.eventEnd]);

  /* ================= ROUTE BUILDER ================= */
  const addRouteStop = () => {
    if (!routeInput.trim()) return;
    const updated = [...routeStops, routeInput.trim()];
    setRouteStops(updated);
    setRouteInput("");
    setForm((f) => ({ ...f, route: `From ${updated.join(" → ")}` }));
  };

  /* ================= HELPERS ================= */
  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const validate = () => {
    if (
      !form.name ||
      !form.phone ||
      !form.location ||
      !form.eventStart ||
      !form.eventEnd ||
      !form.carId ||
      routeStops.length < 2
    )
      return "Please complete all required fields.";

    if (new Date(form.eventEnd) < new Date(form.eventStart))
      return "End date cannot be before start date.";

    if (!/^\+?\d{9,15}$/.test(form.phone.replace(/\s/g, "")))
      return "Invalid phone number.";

    return "";
  };

  /* ================= SUBMIT ================= */
  const submit = async () => {
    const msg = validate();
    if (msg) {
      setError(msg);
      return;
    }

    setLoading(true);
    setError("");

    try {
      await addDoc(collection(db, "bookings"), {
        ...form,
        routeStops,
        status: "Pending",
        createdAt: serverTimestamp(),
      });

      setSuccess(true);
      setForm({
        name: "",
        phone: "",
        email: "",
        location: "",
        eventStart: "",
        eventEnd: "",
        carId: "",
        route: "",
      });
      setRouteStops([]);
      setPage(0);
    } catch {
      setError("Booking failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  /* ================= PAGINATION ================= */
  const totalPages = Math.ceil(cars.length / CARS_PER_PAGE);
  const pagedCars = useMemo(
    () =>
      cars.slice(
        page * CARS_PER_PAGE,
        page * CARS_PER_PAGE + CARS_PER_PAGE
      ),
    [cars, page]
  );

  /* ================= UI ================= */
  return (
    <div
  className="min-h-screen py-16 px-4 relative bg-cover bg-center"
  style={{ backgroundImage: `url(${heroBg})` }}
>
  {/* BACKGROUND OVERLAY */}
  <div
    className={`absolute inset-0 ${
      dark ? "bg-black/70" : "bg-white/80"
    }`}
  />

      <motion.div
  initial={{ opacity: 0, y: 30 }}
  animate={{ opacity: 1, y: 0 }}
  className="relative z-10 max-w-2xl mx-auto rounded-3xl p-10
             bg-[#0f1115] border border-white/10 shadow-2xl text-white"
>

        <h2 className="text-3xl font-bold text-center mb-6">
          Book Hearse Service
        </h2>

        <AnimatePresence>
          {success && (
            <motion.div className="mb-6 flex gap-2 bg-green-500/10 text-green-400 p-4 rounded-xl">
              <CheckCircle /> Booking submitted successfully
            </motion.div>
          )}
        </AnimatePresence>

        {error && (
          <div className="mb-4 bg-red-500/10 text-red-400 p-3 rounded-lg">
            {error}
          </div>
        )}

        <div className="space-y-5">
          {/* CUSTOMER INFO */}
          <Input icon={User} label="Full Name" required theme={theme}
            value={form.name} onChange={(v) => update("name", v)} />

          <Input icon={Phone} label="Phone Number" required theme={theme}
            value={form.phone} onChange={(v) => update("phone", v)} />

          <Input icon={Mail} label="Email (Optional)" theme={theme}
            value={form.email} onChange={(v) => update("email", v)} />

          <Input icon={MapPin} label="Pickup Location" required theme={theme}
            value={form.location} onChange={(v) => update("location", v)} />

          {/* ROUTE */}
          <div>
            <label className="font-medium mb-1 block">Route Builder</label>
            <div className="flex gap-2">
              <input
                value={routeInput}
                onChange={(e) => setRouteInput(e.target.value)}
                placeholder="Enter location"
                className={`flex-1 px-4 py-3 rounded-xl border ${theme.input}`}
              />
              <button
                type="button"
                onClick={addRouteStop}
                className="px-4 rounded-xl bg-sky-600 text-white flex items-center gap-1"
              >
                <Plus size={16} /> Add
              </button>
            </div>
            {routeStops.length > 0 && (
              <p className="text-sm mt-2 text-sky-400">{form.route}</p>
            )}
          </div>

          {/* DATE RANGE */}
          <div className="grid grid-cols-2 gap-4">
            <Input icon={CalendarDays} label="Start Date" type="date" required
              theme={theme} value={form.eventStart}
              onChange={(v) => update("eventStart", v)} />
            <Input icon={CalendarDays} label="End Date" type="date" required
              theme={theme} value={form.eventEnd}
              onChange={(v) => update("eventEnd", v)} />
          </div>

          {/* CARS */}
          <label className="font-medium block">Select Hearse</label>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            {pagedCars.map((car) => {
              const blocked = blockedCars.includes(car.id);
              const selected = form.carId === car.id;

              return (
                <button
                  key={car.id}
                  disabled={blocked}
                  onClick={() => update("carId", car.id)}
                  className={`rounded-xl overflow-hidden border
                    ${selected ? "border-sky-500" : ""}
                    ${blocked ? "opacity-40 cursor-not-allowed" : ""}`}
                >
                  <img
                    src={car.image}
                    alt={car.name}
                    className="h-32 w-full object-cover"
                  />
                  <div className="p-3 text-left">
                    <p className="font-semibold">{car.name}</p>
                    <p className="text-xs text-gray-400">{car.plate}</p>
                    {blocked && (
                      <p className="text-xs text-red-400 mt-1">
                        Booked on your event date(s)
                      </p>
                    )}
                  </div>
                </button>
              );
            })}
          </div>

          {/* PAGINATION */}
          <div className="flex justify-between items-center mt-4">
            <button
              disabled={page === 0}
              onClick={() => setPage((p) => p - 1)}
              className="flex items-center gap-1 px-4 py-2 rounded-xl bg-white/10 disabled:opacity-40"
            >
              <ChevronLeft size={16} /> Previous
            </button>

            <span className="text-sm opacity-70">
              Page {page + 1} of {totalPages}
            </span>

            <button
              disabled={page + 1 >= totalPages}
              onClick={() => setPage((p) => p + 1)}
              className="flex items-center gap-1 px-4 py-2 rounded-xl bg-white/10 disabled:opacity-40"
            >
              Next <ChevronRight size={16} />
            </button>
          </div>

          {/* SUBMIT */}
          <button
            onClick={submit}
            disabled={loading}
            className="w-full mt-6 py-4 rounded-xl bg-sky-600 hover:bg-sky-700 text-white font-semibold"
          >
            {loading ? "Submitting..." : "Submit Booking"}
          </button>
        </div>
      </motion.div>
    </div>
  );
}

/* ================= INPUT ================= */
function Input({ icon: Icon, label, value, onChange, required, theme, type = "text" }) {
  return (
    <div>
      <label className="font-medium mb-1 block">
        {label} {required && <span className="text-red-500">*</span>}
      </label>
      <div className="relative">
        <Icon className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
        <input
          type={type}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className={`w-full pl-10 py-3 rounded-xl border ${theme.input}`}
        />
      </div>
    </div>
  );
}
